import { Component, Input, OnInit, OnChanges, SimpleChanges, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material';
import axios from 'axios';

export interface TelemetryData {
  summary: any;
  aiAssessment: any;
  iecAssessment: any;
  healthAssessment: any;
  trendAssessment: any;
  remainingUsefulLife: any;
  executiveSummary: any;
  recommendations: string[];
  asset: any;
}

export interface CategoryTab {
  key: string;
  label: string;
  data?: any;
  violationsCount: number;
}

@Component({
  selector: 'app-ai-model',
  templateUrl: './ai-model.component.html',
  styleUrls: ['./ai-model.component.scss']
})
export class AiModelComponent implements OnInit, OnChanges {
  
  @Input() requestBody: any = null;

  data: TelemetryData | null = null;
  isLoading: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';

  activeCategory: string = 'all';

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {
    if (this.dialogData) {
      this.requestBody = this.dialogData;
    }
  }

  ngOnInit(): void {
    console.log('AiModelComponent initialized with requestBody:', this.requestBody);
    if (this.requestBody) {
      this.fetchAssessmentData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requestBody'] && !changes['requestBody'].firstChange && this.requestBody) {
      this.fetchAssessmentData();
    }
  }

  async fetchAssessmentData(): Promise<void> {
    if (!this.requestBody) {
      console.warn('AiModelComponent: No requestBody provided.');
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    try {
      console.log('Sending Telemetry Request:', this.requestBody);

      const response = await axios.post<TelemetryData>(
        'http://localhost:8000/predict',
        this.requestBody,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Telemetry Assessment Response:', response.data);
      this.data = response.data;

    } catch (err) {
      console.error('Failed to fetch telemetry assessment:', err);
      this.hasError = true;
      
      const errorObj = err as any;
      if (errorObj && errorObj.response && errorObj.response.data && errorObj.response.data.message) {
        this.errorMessage = errorObj.response.data.message;
      } else {
        this.errorMessage = 'Failed to load telemetry assessment. Please try again.';
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Retrieves overall global violations across all IEC categories
   */
  get violationsList(): any[] {
    if (
      this.data &&
      this.data.iecAssessment &&
      this.data.iecAssessment.overall &&
      this.data.iecAssessment.overall.violations
    ) {
      return this.data.iecAssessment.overall.violations;
    }
    return [];
  }

  /**
   * Helper function to safely extract array length without optional chaining
   */
  private getViolationsCount(categoryObj: any): number {
    if (categoryObj && categoryObj.violations && Array.isArray(categoryObj.violations)) {
      return categoryObj.violations.length;
    }
    return 0;
  }

  /**
   * Dynamic Category Builder: Extracts all IEC categories safely
   */
  get iecCategories(): CategoryTab[] {
    const iec = (this.data && this.data.iecAssessment) ? this.data.iecAssessment : null;
    const globalViolationsCount = this.violationsList.length;

    const baseCategories = [
      { key: 'all', label: 'All Categories', data: iec ? iec.overall : null, violationsCount: globalViolationsCount },
      { key: 'thermal', label: 'Thermal', data: iec ? iec.thermal : null, violationsCount: this.getViolationsCount(iec ? iec.thermal : null) },
      { key: 'electrical', label: 'Electrical', data: iec ? iec.electrical : null, violationsCount: this.getViolationsCount(iec ? iec.electrical : null) },
      { key: 'oil', label: 'Oil Health', data: iec ? iec.oil : null, violationsCount: this.getViolationsCount(iec ? iec.oil : null) },
      { key: 'environment', label: 'Environment', data: iec ? iec.environment : null, violationsCount: this.getViolationsCount(iec ? iec.environment : null) },
      { key: 'loading', label: 'Loading', data: iec ? iec.loading : null, violationsCount: this.getViolationsCount(iec ? iec.loading : null) },
      { key: 'protection', label: 'Protection', data: iec ? iec.protection : null, violationsCount: this.getViolationsCount(iec ? iec.protection : null) }
    ];

    return baseCategories;
  }

  // Alias getter for backward compatibility
  get categories(): CategoryTab[] {
    return this.iecCategories;
  }

  setActiveCategory(categoryKey: string): void {
    this.activeCategory = categoryKey;
  }

  /**
   * Status-to-Hex mapper supporting cyber visual themes
   */
  getColorHex(colorString: string): string {
    if (!colorString) return '#94a3b8';
    const normalized = colorString.toLowerCase();
    
    switch (normalized) {
      case 'green':
      case 'lime':
      case 'normal':
      case 'good':
      case 'low':
      case 'stable':
        return '#30d158';
        
      case 'yellow':
      case 'orange':
      case 'warning':
      case 'medium':
        return '#ffcc00';
        
      case 'red':
      case 'critical':
      case 'high':
      case 'immediate':
        return '#ff3b30';

      case 'cyan':
        return '#00f0ff';
        
      default: 
        return colorString.indexOf('#') === 0 ? colorString : '#94a3b8';
    }
  }

  getStatusColor(status: string): string {
    return this.getColorHex(status);
  }
}